import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { webpSrc, onImgError } from "@/lib/img";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const StrumentiPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // La scheda di «Quanto Mettere da Parte» compare solo quando la vendita è accesa in /admin.
  // Per vederla in anteprima a vendita spenta: /strumenti?anteprima=qmdp
  const [qmdpVendita, setQmdpVendita] = useState(false);
  const qmdpVisibile = qmdpVendita || (typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("anteprima") === "qmdp");

  useEffect(() => {
    axios.get(`${API}/products`).then(r => {
      setProducts(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    axios.get(`${API}/qmdp-stato`).then(r => setQmdpVendita(!!r.data.vendita_attiva)).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-[#F4EFEA] pt-32 pb-20" data-testid="strumenti-page">
      <PageSEO {...SEO.strumenti} />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-gold text-xs tracking-[0.3em] font-bold mb-4">STRUMENTI PROFESSIONALI</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] mb-6">
            I NOSTRI <span className="text-gold">STRUMENTI</span>
          </h1>
          <p className="text-[#4A5568] max-w-2xl mx-auto">
            Strumenti digitali progettati da dottori commercialisti per semplificare la gestione della tua attivita.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Check-up Fiscale — pagina statica servita fuori dalla SPA: usare <a href> */}
            <a
              href="/check-up-fiscale/"
              className="group bg-white border border-[#E8E2D5] rounded-lg overflow-hidden hover:border-gold/50 transition-all hover:scale-[1.02]"
              data-testid="product-card-check-up-fiscale"
            >
              <div className="aspect-video bg-gradient-to-br from-gold/20 to-black relative overflow-hidden">
                <img src={webpSrc("/images/check-up-fiscale/cover.png")} onError={onImgError("/images/check-up-fiscale/cover.png")} alt="Check-up Fiscale" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <FileSpreadsheet className="text-gold" size={32} />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl text-[#0D1527] mb-2 group-hover:text-gold transition-colors">CHECK-UP FISCALE</h3>
                <p className="text-[#64708A] text-sm mb-4 line-clamp-2">Rispondi a poche domande e scopri quali leve di pianificazione fiscale sono applicabili alla tua attività.</p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-3xl text-gold">GRATUITO</span>
                  <span className="text-gold text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                    SCOPRI <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </a>
            {qmdpVisibile && (
              <Link
                to="/strumenti/quanto-mettere-da-parte"
                className="group bg-white border-2 border-[#3E8ED0] rounded-lg overflow-hidden hover:border-[#2F7DBF] transition-all hover:scale-[1.02]"
                data-testid="product-card-quanto-mettere-da-parte"
              >
                <div className="aspect-video relative overflow-hidden bg-gradient-to-br from-[#7CC0EE] to-[#3E8ED0]">
                  <img
                    src="/images/strumenti/quanto-mettere-da-parte.webp"
                    alt="Quanto Mettere da Parte — Forfettari"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover opacity-75 group-hover:opacity-90 transition-opacity"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C5A8C]/70 to-transparent" />
                  <div className="absolute top-4 left-4 bg-[#0D1527] text-[#7CC0EE] text-[10px] font-bold tracking-[0.18em] px-2.5 py-1 rounded">
                    SERVIZIO MENSILE
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <CalendarCheck className="text-white" size={34} strokeWidth={1.6} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-[#0D1527] mb-2 group-hover:text-[#2F7DBF] transition-colors">
                    Quanto Mettere da Parte — Forfettari
                  </h3>
                  <p className="text-[#64708A] text-sm mb-4 line-clamp-2">
                    Ogni mese ci dici quanto hai incassato, noi ti diciamo quanto tenere da parte per tasse e contributi.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-3xl text-[#2F7DBF]">9,90 € <span className="text-sm">al mese IVA incl.</span></span>
                    <span className="text-[#2F7DBF] text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                      SCOPRI <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
              </Link>
            )}
            {/* Portale documenti — area riservata clienti */}
            <Link
              to="/portale-documenti"
              className="group bg-white border border-[#E8E2D5] rounded-lg overflow-hidden hover:border-gold/50 transition-all hover:scale-[1.02]"
              data-testid="product-card-portale-documenti"
            >
              <div className="aspect-video relative overflow-hidden bg-[#0D1527]">
                <img
                  src="/images/strumenti/portale-documenti.webp"
                  alt="Portale documenti"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-85 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1527] to-transparent" />
                <div className="absolute top-4 left-4 bg-[#0D1527] border border-gold/40 text-gold text-[10px] font-bold tracking-[0.18em] px-2.5 py-1 rounded">
                  AREA RISERVATA
                </div>
                <div className="absolute bottom-4 left-4">
                  <FileCheck className="text-gold" size={32} />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl text-[#0D1527] mb-2 group-hover:text-gold transition-colors">
                  Portale documenti
                </h3>
                <p className="text-[#64708A] text-sm mb-4 line-clamp-2">
                  Carica e consulta i documenti dello studio in un posto solo: fatture, contratti, estratti conto.
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-xl text-gold">COMPRESO <span className="text-sm">negli abbonamenti</span></span>
                  <span className="text-gold text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                    ACCEDI <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
            {products.filter(p => !p.hide_from_catalog).map(product => (
              <Link
                key={product.id}
                to={`/strumenti/${product.slug}`}
                className="group bg-white border border-[#E8E2D5] rounded-lg overflow-hidden hover:border-gold/50 transition-all hover:scale-[1.02]"
                data-testid={`product-card-${product.slug}`}
              >
                <div className="aspect-video bg-gradient-to-br from-gold/20 to-black relative overflow-hidden">
                  {product.image_url && (
                    <img src={webpSrc(product.image_url)} onError={onImgError(product.image_url)} alt={product.title} loading="lazy" decoding="async" className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <div className="absolute top-4 left-4 bg-[#0D1527] text-[#E8E2D5] text-[10px] font-bold tracking-[0.18em] px-2.5 py-1 rounded">
                    FILE EXCEL
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <FileSpreadsheet className="text-gold" size={32} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-[#0D1527] mb-2 group-hover:text-gold transition-colors">{product.title}</h3>
                  <p className="text-[#64708A] text-sm mb-4 line-clamp-2">{product.short_description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-3xl text-gold">{product.price.toFixed(2)} <span className="text-lg">EUR</span></span>
                    <span className="text-gold text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                      SCOPRI <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {/* Valutazione CPB — landing statica, in fondo all'elenco */}
            <Link
              to="/strumenti/valutazione-concordato-preventivo-biennale/"
              reloadDocument
              className="group bg-white border border-gold/40 rounded-lg overflow-hidden hover:border-gold transition-all hover:scale-[1.02] relative"
              data-testid="product-card-valutazione-cpb"
            >
              <div className="aspect-video bg-[#FAF8F5] relative overflow-hidden">
                <img src={webpSrc("/images/products/valutazione-cpb.png")} onError={onImgError("/images/products/valutazione-cpb.png")} alt="Valutazione CPB" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl text-[#0D1527] mb-2 group-hover:text-gold transition-colors">Valutazione Concordato Preventivo Biennale</h3>
                <p className="text-[#64708A] text-sm mb-4 line-clamp-2">Il calcolo esatto delle imposte che pagherai con il concordato e senza, sui tuoi numeri reali. Report entro una settimana.</p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-xl text-gold">da 47 <span className="text-base">EUR</span></span>
                  <span className="text-gold text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                    SCOPRI COME FUNZIONA <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};


export { StrumentiPage };
